import {useState} from 'react';
import {useWorkspace} from '../workspace/WorkspaceContext';
import {api} from '../api';
import {GmailOperation, OAuthStartResult, SyncAcknowledgement} from '../types';
import {hasSyncSnapshot} from '../services/gmailResponses';
import {errorMessage} from '../errors';
import {toast} from '../components/ui/sonner';

export function useGmailConnection() {
  const {gmail, refreshGmail, applyGmailSync, refreshInbox} = useWorkspace();
  const [busy, setBusy] = useState<GmailOperation>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  async function connect(): Promise<void> {
    setBusy('connect');
    try { const result = await api<OAuthStartResult>('/oauth/gmail/start', {method: 'POST'}); window.location.assign(result.url); }
    catch (error: unknown) { toast.error(errorMessage(error)); setBusy(null); }
  }

  async function sync(): Promise<void> {
    setBusy('sync');
    try {
      const result = await api<SyncAcknowledgement>('/gmail/sync', {method: 'POST'});
      if (hasSyncSnapshot(result)) applyGmailSync(result);
      else await refreshInbox();
      if (result.warning) toast.warning(result.warning);
      else toast.success(result.imported ? `${result.imported} messages added to your inbox` : 'Your inbox is up to date');
    } catch (error: unknown) {
      toast.error(errorMessage(error));
      await refreshGmail().catch(refreshError => toast.error(errorMessage(refreshError)));
    } finally { setBusy(null); }
  }

  async function disconnect(): Promise<void> {
    setBusy('disconnect');
    try { await api('/gmail', {method: 'DELETE'}); await refreshGmail(); setDisconnectOpen(false); toast.success('Gmail disconnected'); }
    catch (error: unknown) { toast.error(errorMessage(error)); }
    finally { setBusy(null); }
  }

  async function autoSync(enabled: boolean): Promise<void> {
    setBusy('settings');
    try {
      await api('/gmail/settings', {method: 'PATCH', body: JSON.stringify({auto_sync: enabled})});
      await refreshGmail(); toast.success(enabled ? 'Automatic sync enabled' : 'Automatic sync paused');
    } catch (error: unknown) { toast.error(errorMessage(error)); }
    finally { setBusy(null); }
  }

  return {gmail, busy, disconnectOpen, setDisconnectOpen, connect, sync, disconnect, autoSync};
}
export type GmailController = ReturnType<typeof useGmailConnection>;